import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {AfterViewInit, Component, DoCheck, ElementRef, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import { NgxPopperjsDirective } from 'ngx-popperjs';
import {ControlValueAccessor, UntypedFormControl, NgControl} from '@angular/forms';

@Component({
	selector: 'app-text-input',
	templateUrl: './text-input.component.html',
	providers: [
		{
			provide: [],
			useExisting: forwardRef(() => TextInputComponent),
			multi: true,
		},
	],
})
export class TextInputComponent implements OnInit, OnChanges, DoCheck, OnDestroy, AfterViewInit, ControlValueAccessor {
	@Input() label?: string;
	@Input() placeholder = '';
	@Input() type = 'text';
	@Input() disabled = false;
	@Input() icon?: string;
	@Input() width?: string;
	@Input() inline = false;
	@Input() configuration = false;
	@Input() transparent = false;
	@Input() trapFocus = false;
	@Input() name?: string;
	@Input() autocomplete?: string;
	@Input() hint?: string;
	/** What the field measures in mm, e.g. 'De breedte', for its range errors. */
	@Input() measure?: string;
	@Input() validateOn: 'touched' | 'change' = 'touched';
	/**
	 * Renders the validation messages under the field instead of in the hover
	 * tooltip. Required wherever a touch device must be able to read them.
	 */
	@Input() inlineErrors = false;

	@ViewChild('inputRef') inputRef: ElementRef<HTMLInputElement>;
	@ViewChild('errorPopper') errorPopper: NgxPopperjsDirective;
	private errorsShown = false;
	private liveValue: unknown = undefined;

	@Output() blurInputField: EventEmitter<unknown> = new EventEmitter();
	@Output() keyEnter: EventEmitter<void> = new EventEmitter();
	@Output() keyEscape: EventEmitter<void> = new EventEmitter();

	private componentDestroyed$: Subject<boolean> = new Subject<boolean>();

	public control: UntypedFormControl = new UntypedFormControl(null);
	public updateValue = (_: any) => {};
	public onTouched = () => {};

	constructor(public ngControl: NgControl) {
		ngControl.valueAccessor = this;
	}

	private propagateChange(value: any): void {
		if (this.updateValue) {
			return this.updateValue(value);
		}

		if (this.control) {
			this.control.setValue(value, {
				emitEvent: false
			});
		}
	}

	public onBlurField(): void {
		this.onTouched();
		this.blurInputField.emit(this.control.value);
	}

	public ngAfterViewInit(): void {
		if (this.trapFocus && this.inputRef) {
			setTimeout(() => this.inputRef.nativeElement.focus());
		}
	}

	public ngOnChanges(changes: SimpleChanges): void {
		// `disabled` is a binding, not a one-off: a field can become read-only after init
		// when the selection changes under it. Reading it only in ngOnInit left the input
		// editable for everything selected afterwards.
		if (changes['disabled']) {
			this.setDisabledState(this.disabled);
		}
	}

	public ngOnInit(): void {
		this.control.valueChanges.pipe(
			takeUntil(this.componentDestroyed$),
		).subscribe((value) => {
			this.propagateChange(value);
		});
	}

	public ngOnDestroy(): void {
		this.componentDestroyed$.next(true);
		this.componentDestroyed$.complete();
	}

	public writeValue(value: any): void {
		setTimeout(() => this.control.setValue(value, {
			emitEvent: false
		}));
	}

	public registerOnChange(fn: any): void {
		this.updateValue = fn;
	}

	public registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		if (isDisabled) {
			this.control.disable({ emitEvent: false });
		} else {
			this.control.enable({ emitEvent: false });
		}
	}

	// The popper library ignores hide() once it is disabled, so an open error popper would
	// stay on screen, empty, after the value became valid: close it while it still listens.
	// While the user types a refused value the reason opens by itself, without a hover; it is
	// opened after the bindings ran, because the popper is still disabled during this check.
	public ngDoCheck(): void {
		if (this.errorsShown && !this.showErrors) {
			this.errorPopper?.hide();
		}
		this.errorsShown = this.showErrors;

		const typing = this.showErrors && !this.inlineErrors && this.inputRef?.nativeElement === document.activeElement;
		if (typing && this.liveValue !== this.control.value) {
			queueMicrotask(() => {
				if (this.showErrors && this.inputRef?.nativeElement === document.activeElement) {
					this.errorPopper?.show();
				}
			});
		} else if (!typing && this.liveValue !== undefined && this.showErrors) {
			this.errorPopper?.hide();
		}
		this.liveValue = typing ? this.control.value : undefined;
	}

	public get showErrors(): boolean {
		if (!this.ngControl.invalid) {
			return false;
		}
		if (this.validateOn === 'change') {
			return this.ngControl.touched || this.ngControl.dirty;
		}
		return this.ngControl.touched;
	}

	public onKeyEnter(): void {
		if (this.disabled) {
			return;
		}
		if (this.ngControl.valid) {
			this.keyEnter.emit();
		}
	}

}
